import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, FileText, Download, ArrowRight, Shield, Zap, Award, BookOpen, Calculator } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { NavBar, Footer } from "../components/Layout";
import { trackEvent } from "../hooks/usePageTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA = {
  "math-sec4-sn": {
    title: "Mathématiques SN4", subtitle: "Sciences Naturelles - Secondaire 4", price: "26,99",
    cover: "https://customer-assets.emergentagent.com/job_study-docs-1c/artifacts/xda5n1zx_IMG_1772.jpeg",
    chapters: [
      { part: "A", title: "Algèbre", items: ["Polynômes et factorisation", "Expressions rationnelles", "Équations et inéquations du 2e degré", "Systèmes d'équations", "Fonctions quadratiques et partie entière"] },
      { part: "B", title: "Géométrie", items: ["Similitude et isométrie des triangles", "Figures et solides équivalents", "Trigonométrie du triangle rectangle", "Trigonométrie du triangle quelconque"] },
      { part: "C", title: "Géométrie analytique", items: ["Distance, point milieu, pente", "Positions relatives et cercles", "Demi-plans et inéquations"] },
      { part: "D", title: "Statistiques", items: ["Nuages de points et corrélation", "Droites de régression"] },
    ]
  },
  "math-sec4-cst": {
    title: "Mathématiques CST4", subtitle: "Culture, Société et Technique - Secondaire 4", price: "24,99",
    cover: "https://customer-assets.emergentagent.com/job_study-docs-1c/artifacts/674sas0x_IMG_1774.jpeg",
    chapters: [
      { part: "A", title: "Algèbre", items: ["Résolution d'équations", "Systèmes d'équations", "Fonctions et paramètres", "Fonction quadratique", "Fonction partie entière"] },
      { part: "B", title: "Géométrie", items: ["Similitude et isométrie", "Figures et solides équivalents", "Trigonométrie"] },
      { part: "C", title: "Statistiques et probabilités", items: ["Nuages de points", "Corrélation et régression", "Probabilités conditionnelles"] },
    ]
  }
};

const partColors = ["text-amber-400 bg-amber-500/20", "text-blue-400 bg-blue-500/20", "text-purple-400 bg-purple-500/20", "text-emerald-400 bg-emerald-500/20"];

const FAQ_DATA = {
  "math-sec4-sn": [
    { q: "Que contient le cahier de révision mathématiques SN4?", a: "Le guide contient 120+ pages réparties en 17 chapitres complets couvrant tout le programme de mathématiques Sciences Naturelles du secondaire 4 au Québec : algèbre, géométrie, géométrie analytique et statistiques. Il inclut plus de 300 exercices résolus avec solutions détaillées et 3 examens blancs au format ministériel." },
    { q: "Est-ce conforme au programme du Ministère de l'Éducation du Québec?", a: "Oui, le guide est entièrement aligné avec le programme du Ministère de l'Éducation du Québec pour les mathématiques SN (Sciences Naturelles) du secondaire 4. Chaque chapitre suit les compétences et les notions exigées par le programme." },
    { q: "Comment recevoir le guide après l'achat?", a: "Après le paiement sécurisé par carte via Stripe (26,99$ CAD), tu reçois immédiatement un lien de téléchargement en format PDF. Aucun compte n'est nécessaire, c'est un téléchargement direct et instantané." },
    { q: "Le guide aide-t-il à préparer l'examen du ministère en math?", a: "Absolument! Le guide inclut 3 examens blancs complets au format ministériel, des résumés théoriques par chapitre et plus de 300 exercices progressifs. C'est l'outil idéal pour la préparation à l'examen du ministère en mathématiques SN4." },
    { q: "Quelle est la différence entre SN4 et CST4?", a: "Le programme SN4 (Sciences Naturelles) est plus avancé et couvre l'algèbre approfondie, la géométrie analytique et la trigonométrie. Le CST4 (Culture, Société et Technique) est orienté vers les applications pratiques comme les statistiques, probabilités et fonctions appliquées. Choisis selon ton parcours scolaire." },
  ],
  "math-sec4-cst": [
    { q: "Que contient le cahier de révision mathématiques CST4?", a: "Le guide couvre l'intégralité du programme de mathématiques CST (Culture, Société et Technique) du secondaire 4 au Québec. Il inclut des exercices résolus avec solutions détaillées pour chaque notion : algèbre, géométrie, statistiques et probabilités." },
    { q: "Est-ce conforme au programme du Ministère de l'Éducation du Québec?", a: "Oui, le guide est entièrement conforme au programme du Ministère de l'Éducation du Québec pour les mathématiques CST du secondaire 4. Toutes les compétences requises sont couvertes." },
    { q: "Comment recevoir le guide après l'achat?", a: "Après le paiement sécurisé par carte via Stripe (24,99$ CAD), tu reçois immédiatement un lien de téléchargement en format PDF. Aucun compte n'est nécessaire." },
    { q: "Le guide est-il adapté pour l'examen du ministère CST4?", a: "Oui, le guide est spécifiquement conçu pour préparer l'examen du ministère en mathématiques CST4. Il couvre toutes les notions exigées avec des exercices progressifs et des examens de pratique." },
    { q: "Existe-t-il un aide-mémoire gratuit CST4?", a: "Oui! Nous offrons un aide-mémoire gratuit en PDF qui résume les formules essentielles du programme CST4. Rendez-vous sur notre page de téléchargement gratuit pour l'obtenir." },
  ]
};

const ProductPage = ({ productId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const d = DATA[productId];
  if (!d) return <div className="min-h-screen flex items-center justify-center text-white">Produit non trouvé</div>;

  const navKey = productId === "math-sec4-sn" ? "sn4" : "cst4";

  const handleBuyNow = async () => {
    setIsLoading(true);
    trackEvent(`/${navKey}`, `click_buy_${navKey}`);
    try {
      const originUrl = window.location.origin;
      const r = await axios.post(`${API}/checkout/create-session`, { product_id: productId, origin_url: originUrl });
      window.location.href = r.data.url;
    } catch {
      toast.error("Erreur lors du paiement. Réessaie.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <NavBar active={navKey} />

      {/* Hero */}
      <section className="relative py-16 sm:py-24 overflow-hidden gradient-hero grid-overlay">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4"></div>
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="badge-gold mb-6">Conforme au programme du Québec</div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                {d.title.split(" ")[0]}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">{d.title.split(" ").slice(1).join(" ")}</span>
              </h1>
              <p className="text-lg text-blue-200/70 mb-4 max-w-lg">{d.subtitle} - Tout le programme avec exercices corrigés.</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-8 max-w-lg backdrop-blur-sm">
                <p className="text-blue-100/80 text-sm">
                  <strong className="text-amber-400">Notre approche pédagogique</strong><br/><br/>Nous privilégions des questions courtes, claires et simplifiées qui vont directement à l'essentiel. Notre objectif est d'amener l'élève à développer sa capacité à décomposer un problème complexe en une série de questions simples. Cette méthode favorise une meilleure compréhension, facilite la résolution des problèmes et renforce l'autonomie de l'élève.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                <button data-testid="buy-now-hero-button" onClick={handleBuyNow} disabled={isLoading} className="btn-gold flex items-center gap-2 text-lg px-8 py-4">
                  {isLoading ? <div className="spinner"></div> : <>Acheter maintenant <ArrowRight className="w-5 h-5" /></>}
                </button>
                <div>
                  <span className="text-3xl font-bold text-amber-400">{d.price}$</span>
                  <span className="text-blue-300/50 ml-1">CAD</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-sm text-blue-200/60"><CheckCircle className="w-5 h-5 text-emerald-400" /> Téléchargement immédiat</div>
                <div className="flex items-center gap-2 text-sm text-blue-200/60"><Shield className="w-5 h-5 text-blue-400" /> Paiement sécurisé</div>
                <div className="flex items-center gap-2 text-sm text-blue-200/60"><Zap className="w-5 h-5 text-amber-400" /> Format PDF</div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex justify-center">
              <div className="relative max-w-sm book-mockup">
                <motion.div animate={{ y: [-5, 5, -5] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-6 -right-6 w-14 h-14 bg-amber-500/60 rounded-xl rotate-12 shadow-lg shadow-amber-500/20 z-10" />
                <motion.div animate={{ y: [5, -5, 5] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-4 -left-4 w-10 h-10 bg-blue-500/60 rounded-full shadow-lg shadow-blue-500/20 z-10" />
                <div className="book-mockup-inner">
                  <div className="relative aspect-[1/1.5] rounded-2xl shadow-2xl overflow-hidden glow-gold border border-white/10">
                    <img src={d.cover} alt={`Cahier de révision ${d.title} - exercices corrigés et préparation examen ministère secondaire 4 Québec`} className="w-full h-full object-cover" loading="eager" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-navy-800 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: BookOpen, title: "Chapitres complets", desc: "Tout le programme couvert" },
            { icon: Calculator, title: "Exercices résolus", desc: "Solutions étape par étape" },
            { icon: FileText, title: "Examens blancs", desc: "Format ministériel" },
            { icon: Award, title: "Programme QC", desc: "Ministère de l'Éducation" },
          ].map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="text-center p-6 bg-white/5 rounded-2xl border border-white/5">
              <f.icon className="w-8 h-8 text-amber-400 mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-1">{f.title}</h3>
              <p className="text-blue-200/60 text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-white text-center mb-12">Programme complet : exercices de mathématiques secondaire 4</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {d.chapters.map((ch, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="card-dark hover:border-white/20 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${partColors[i] || partColors[0]} rounded-lg flex items-center justify-center`}>
                    <span className="font-bold">{ch.part}</span>
                  </div>
                  <h3 className="font-semibold text-xl text-white">{ch.title}</h3>
                </div>
                <ul className="space-y-3">
                  {ch.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-blue-200/70">
                      <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SEO Section */}
      <section className="py-20 bg-navy-800" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-display font-bold text-3xl text-white text-center mb-12">Questions fréquentes</h2>
          <div className="space-y-4">
            {FAQ_DATA[productId]?.map((faq, i) => (
              <details key={i} className="card-dark group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <summary className="cursor-pointer font-semibold text-white flex items-center justify-between" itemProp="name">
                  {faq.q}
                  <span className="text-amber-400 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <div className="mt-4 text-blue-200/70 leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Linking / Cross-sell */}
      <section className="py-16 bg-navy-900 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display font-bold text-2xl text-white mb-6">Autres ressources pédagogiques</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {productId === "math-sec4-sn" ? (
              <Link to="/cst4" className="card-dark inline-flex items-center gap-3 hover:border-amber-500/30 transition-all">
                <img src={DATA["math-sec4-cst"].cover} alt="Guide mathématiques CST4 secondaire 4 Québec" className="h-20 rounded-lg" />
                <div className="text-left">
                  <p className="font-semibold text-white">Mathématiques CST4</p>
                  <p className="text-amber-400 font-bold">24,99$ CAD</p>
                </div>
              </Link>
            ) : (
              <Link to="/sn4" className="card-dark inline-flex items-center gap-3 hover:border-amber-500/30 transition-all">
                <img src={DATA["math-sec4-sn"].cover} alt="Guide mathématiques SN4 secondaire 4 Québec" className="h-20 rounded-lg" />
                <div className="text-left">
                  <p className="font-semibold text-white">Mathématiques SN4</p>
                  <p className="text-amber-400 font-bold">26,99$ CAD</p>
                </div>
              </Link>
            )}
            <Link to="/gratuit" className="card-dark inline-flex items-center gap-3 hover:border-emerald-500/30 transition-all">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Download className="w-8 h-8 text-emerald-400" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-white">Aide-mémoire gratuit</p>
                <p className="text-emerald-400 font-bold">Téléchargement gratuit</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-gradient-to-br from-amber-600 via-amber-500 to-yellow-500 overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-6">Prêt pour l'examen du ministère en math secondaire 4?</h2>
          <p className="text-lg text-slate-800 mb-8">Cahier d'exercices corrigés, paiement sécurisé, téléchargement PDF immédiat.</p>
          <button data-testid="buy-now-cta-button" onClick={handleBuyNow} disabled={isLoading}
            className="bg-slate-900 text-white font-bold px-10 py-4 rounded-lg hover:bg-slate-800 transition-all shadow-xl flex items-center gap-3 mx-auto text-lg">
            {isLoading ? <div className="spinner"></div> : <><Download className="w-6 h-6" /> Acheter - {d.price}$ CAD</>}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductPage;
